import { useMemo, useState, useEffect, useCallback } from 'react';
import {
	type JSMProjectTemplateKey,
	INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY,
} from '@atlassian/jira-common-constants/src/project-template-type-keys';
import { VAR1, VAR2 } from '@atlassian/jira-common-util-get-experiment-group/src';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { performGetRequest } from '@atlassian/jira-fetch';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectProperties } from '@atlassian/jira-project-properties-services';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import type { Query } from '@atlassian/jira-router';
import { useGettingStartedPanelState } from '@atlassian/jira-servicedesk-getting-started-panel-state';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import {
	DISABLE_NPLE_PROJECT_PROJECT_KEY,
	ERROR_CODE_NOT_FOUND,
	GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES,
	GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_ITSM,
	GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_PROD,
	PACKAGE_NAME,
	PACKAGE_TEAM_NAME,
} from '../common/constants';
import type {
	GettingStartedPageStateProps,
	ExcludedGettingStartedPageTemplatesType,
} from '../common/types';
import {
	getAllFeatures,
	getCustomersData,
	getLandingPageState,
	initialiseLandingPageState,
	updateLandingPageStateProperties,
	updateTaskCompletion,
} from './getting-started-page-state';

export const useGettingStartedPageStateActions = () => {
	const baseUrl = window.location.origin;
	const { cloudId } = useTenantContext();
	const { data } = useProjectContext() || {};
	const { projectId, projectKey } = data || {};
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const projectIdString = projectId ? projectId.toString() : '';
	const projectKeyString = projectKey ? projectKey.toString() : '';

	return useMemo(() => {
		const actionProps: GettingStartedPageStateProps = {
			baseUrl,
			cloudId,
			projectId: projectIdString,
			createAnalyticsEvent,
		};
		return [
			{
				updateTaskCompletion: updateTaskCompletion(actionProps),
				updateLandingPageStateProperties: updateLandingPageStateProperties(actionProps),
				getLandingPageState: getLandingPageState(actionProps),
				initialiseLandingPageState: initialiseLandingPageState(actionProps),
				getManageExternalInformationData: {
					getAllFeatures: getAllFeatures({
						baseUrl,
						projectId: projectIdString,
						createAnalyticsEvent,
					}),
					getCustomerData: getCustomersData(baseUrl, projectKeyString),
				},
			},
		];
	}, [baseUrl, cloudId, createAnalyticsEvent, projectIdString, projectKeyString]);
};

export const useIsInCorrectGSPProject = (): null | boolean => {
	const [{ data: gspStateData }] = useGettingStartedPanelState();
	const gspProjectId = gspStateData?.properties?.user?.projectId;
	const { data: containerContext } = useProjectContext();

	if (gspStateData === undefined) {
		return null;
	}

	if (gspProjectId !== undefined && containerContext?.projectId !== undefined) {
		return gspProjectId === `${containerContext.projectId}`;
	}
	return false;
};
export const useIsGettingStartedPageExcludedProject = (): [boolean, boolean] => {
	const { [INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY]: initialProjectTemplate } =
		useProjectProperties<{
			[INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY]: JSMProjectTemplateKey;
		}>([INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY]);
	const isLoading = initialProjectTemplate === undefined;
	let excludedProjectTemplates;
	const isInCorrectProject = useIsInCorrectGSPProject();

	if (ff('getting_started_page_production_rollout')) {
		excludedProjectTemplates =
			isInCorrectProject &&
			expValEquals('jsm_getting_started_page_for_nple_exp', 'cohort', 'variation')
				? GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_ITSM
				: GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_PROD;
	} else {
		excludedProjectTemplates = GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES;
	}

	return useMemo(
		() => [
			isLoading,
			!!initialProjectTemplate &&
				excludedProjectTemplates.includes(
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					initialProjectTemplate as ExcludedGettingStartedPageTemplatesType,
				),
		],
		[excludedProjectTemplates, initialProjectTemplate, isLoading],
	);
};

export const useGetNpleDisabledProjectProperty = (projectKeyOrId: number | string | undefined) => {
	const [npleDisabled, setNpleDisabled] = useState<boolean | undefined>(
		projectKeyOrId ? undefined : true,
	);

	const isLoading = npleDisabled === undefined;
	const getNpleDisabledProjectProperty = useCallback(async () => {
		try {
			const response = await performGetRequest(
				`/rest/api/3/project/${projectKeyOrId}/properties/${DISABLE_NPLE_PROJECT_PROJECT_KEY}`,
			);
			setNpleDisabled(response.value);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setNpleDisabled(error.statusCode !== 404);
			error.statusCode !== 404 &&
				fireErrorAnalytics({
					meta: {
						id: 'getDisableNpleProjectProperty',
						packageName: PACKAGE_NAME,
						teamName: PACKAGE_TEAM_NAME,
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
		}
	}, [projectKeyOrId]);

	useEffect(() => {
		if (projectKeyOrId) {
			getNpleDisabledProjectProperty();
		}
	}, [getNpleDisabledProjectProperty, projectKeyOrId]);

	return [isLoading, npleDisabled];
};

export const useIsGettingStartedPageEnabled = () => {
	const { data } = useProjectContext() || {};
	const isNpleProductionised = ff('getting_started_page_production_rollout');
	const { simplified: isSimplified = false, isProjectAdmin, projectKey } = data || {};
	const [isLoading, isExcludedProject] = useIsGettingStartedPageExcludedProject();
	const [isGetNpleDisabledLoading, npleDisabled] = useGetNpleDisabledProjectProperty(
		isNpleProductionised ? projectKey : undefined,
	);
	const isDataLoading = isLoading || (isGetNpleDisabledLoading && isNpleProductionised);

	return useMemo(
		() => [
			isDataLoading,
			!isDataLoading
				? isProjectAdmin &&
					!isSimplified &&
					!isExcludedProject &&
					((!npleDisabled && isNpleProductionised) ||
						[VAR1, VAR2].includes(
							expVal('jsm_getting_started_page_fast_follows', 'cohort', 'not-enrolled'),
						))
				: false,
		],
		[
			isDataLoading,
			isProjectAdmin,
			isSimplified,
			isExcludedProject,
			npleDisabled,
			isNpleProductionised,
		],
	);
};

export const useRedirectToGettingStartedPage = (queryParams: Query | undefined) => {
	const [isLoading, isGettingStatedPageEnabled] = useIsGettingStartedPageEnabled();
	const [redirectState, setRedirectState] = useState({ loading: true, shouldRedirect: false });
	const [stateActions] = useGettingStartedPageStateActions();
	const referredByGSP =
		queryParams?.referrer === 'gettingStartedPage' ||
		queryParams?.referrer === 'gettingStartedPageLearnByDoing';

	useEffect(() => {
		if (ff('getting_started_page_production_rollout')) {
			return setRedirectState({ loading: false, shouldRedirect: false });
		}

		if (isLoading) {
			return;
		}

		if (isGettingStatedPageEnabled && !referredByGSP) {
			// This is being made serially to avoid slowing down the experience for customers who aren't in the target
			// audience or projects which don't support getting started page.
			stateActions
				.getLandingPageState()
				.then(({ completedItems }) => {
					setRedirectState({ loading: false, shouldRedirect: completedItems.length < 3 });
				}) // eslint-disable-next-line @typescript-eslint/no-explicit-any
				.catch((error): any => {
					// if we receive a 404 error, we create a new completed item
					// list for the user
					if (error.statusCode === ERROR_CODE_NOT_FOUND) {
						stateActions.initialiseLandingPageState();
					}
					setRedirectState({ loading: false, shouldRedirect: true });
				});
		} else {
			setRedirectState({ loading: false, shouldRedirect: false });
		}
	}, [isLoading, isGettingStatedPageEnabled, stateActions, referredByGSP]);

	return redirectState;
};
